import React, { createContext, FC, useMemo, useState } from 'react';
import { createUseContext } from '../../../../context/utils';
import {
  FreelancerTimezone,
  CountriesAndRegions,
  FreelancerLanguagePreference,
  IdealCandidatePreferences,
  FreelancerType,
  ChosenCategory,
} from './types';

export interface IdealCandidateContextProviderProps {
  onChange: (content: string) => void;
  onEditEnd: () => void;
  onCancel: () => void;
  content: IdealCandidatePreferences;
  isLastIncludedSection?: boolean;
}

interface IdealCandidateContextProps extends IdealCandidateContextProviderProps {
  hasChanges: boolean;
  skillsAndExperience: string;
  languages: FreelancerLanguagePreference[];
  countriesAndRegions: CountriesAndRegions;
  timezone: FreelancerTimezone;
  freelancerType: FreelancerType;
  chosenCategory: ChosenCategory;
  setSkillsAndExperience: (skillsAndExperience: string) => void;
  setLanguages: (languages: FreelancerLanguagePreference[]) => void;
  setCountriesAndRegions: (countriesAndRegions: CountriesAndRegions) => void;
  setTimezone: (timezone: FreelancerTimezone) => void;
  setFreelancerType: (freelancerType: FreelancerType) => void;
  setChosenCategory: (chosenCategory: ChosenCategory) => void;
}

export const IdealCandidateContext = createContext<IdealCandidateContextProps | null>(null);

IdealCandidateContext.displayName = 'IdealCandidateContext';

export const IdealCandidateContextProvider: FC<IdealCandidateContextProviderProps> = ({
  children,
  onChange,
  onEditEnd,
  onCancel,
  content,
  isLastIncludedSection: shouldRemoveSection,
}) => {
  const {
    languages: initialLanguages = [],
    countries: initialCountries = [],
    regions: initialRegions = [],
    timezone: initialTimezone = { timezoneId: '', maxOffsetHours: 1 },
    freelancerType: initialFreelancerType,
    skillsAndExperience: initialSkillsAndExperience = '',
  } = content;

  const [languages, setLanguages] = useState<FreelancerLanguagePreference[]>(initialLanguages);
  const [countriesAndRegions, setCountriesAndRegions] = useState<CountriesAndRegions>({
    countries: initialCountries,
    regions: initialRegions,
  });
  const [timezone, setTimezone] = useState<FreelancerTimezone>(initialTimezone);
  const [freelancerType, setFreelancerType] = useState<FreelancerType>(initialFreelancerType as FreelancerType);
  const [skillsAndExperience, setSkillsAndExperience] = useState<string>(initialSkillsAndExperience);
  const [chosenCategory, setChosenCategory] = useState<ChosenCategory>(timezone.timezoneId ? 'timezone' : 'location');

  // Check if any content exists in the section
  const hasContentExists = useMemo(() => {
    const hasSkillsContent = !!skillsAndExperience;
    const hasLanguagesContent = languages.some((lang) => lang.languageCode !== 'zz' && lang.languageCode !== '');
    const hasLocationContent = countriesAndRegions.countries.length > 0 || countriesAndRegions.regions.length > 0;
    const hasTimezoneContent = !!timezone.timezoneId;
    const hasFreelancerTypeContent = !!freelancerType;

    return (
      hasSkillsContent || hasLanguagesContent || hasLocationContent || hasTimezoneContent || hasFreelancerTypeContent
    );
  }, [skillsAndExperience, languages, countriesAndRegions, timezone, freelancerType]);

  const hasChanges = useMemo(() => {
    // If section was just added back and has content, don't disable save
    if (shouldRemoveSection && hasContentExists) {
      return true;
    }

    return hasContentExists;
  }, [shouldRemoveSection, hasContentExists]);

  const handleSave = () => {
    const newLanguages = languages?.filter((language) => language.languageCode !== 'zz');
    setLanguages(newLanguages);
    if (!hasChanges) {
      return;
    }

    const newLocationOrTimezone =
      chosenCategory === 'location'
        ? {
            countries: countriesAndRegions.countries,
            regions: countriesAndRegions.regions,
          }
        : { ...(timezone.timezoneId && { timezone }) };

    const newContent = {
      ...newLocationOrTimezone,
      languages: newLanguages,
      ...(freelancerType && { freelancerType }),
      skillsAndExperience,
    };

    onChange(newContent as any);
    onEditEnd?.();
  };

  const handleCancel = () => {
    setSkillsAndExperience(initialSkillsAndExperience);
    setLanguages(initialLanguages);
    setCountriesAndRegions({
      countries: initialCountries,
      regions: initialRegions,
    });
    setTimezone(initialTimezone as FreelancerTimezone);
    setFreelancerType(initialFreelancerType as FreelancerType);
    onCancel?.();
  };

  return (
    <IdealCandidateContext.Provider
      value={{
        onChange,
        onEditEnd: handleSave,
        onCancel: handleCancel,
        hasChanges,
        content,
        skillsAndExperience,
        languages,
        countriesAndRegions,
        timezone,
        freelancerType,
        chosenCategory,
        setSkillsAndExperience,
        setLanguages,
        setCountriesAndRegions,
        setTimezone,
        setFreelancerType,
        setChosenCategory,
      }}
    >
      {children}
    </IdealCandidateContext.Provider>
  );
};

export const useIdealCandidateContext = createUseContext(IdealCandidateContext);
